import { useNavigate } from 'react-router-dom';
import { COLORS } from '../../constants/theme';
import { formatNumber } from '../../utils/format';
import { isProtected } from '../../utils/protection';
import { RANKING_STATS, rankingStatValue } from '../../constants/rankings';
import type { RankingEntry, RankingStatKey } from '../../types/rankings';

interface RankingsTableProps {
  entries: RankingEntry[];
  viewerEntry: RankingEntry | null;
  currentTick: number;
  sortBy: RankingStatKey;
  onSortChange: (key: RankingStatKey) => void;
}

const YOU_TINT = 'rgba(240, 189, 60, 0.09)';

/**
 * SF-159 Component 4: bespoke desktop table — visually/structurally mirrors `SortableTable`'s
 * conventions (`<button>`-in-`<th>` with `aria-sort`, alternating row background) but owns its own
 * render logic, deliberately not routed through `SortableTable`/`useSortState`
 * (Phase 3 Critical Issues 1 and 2 — see spec Design Source / Key Implementation Note 8):
 *
 * - Sort is fixed-descending only, entirely page-owned. Clicking a header calls
 *   `onSortChange(key)` unconditionally — no direction toggle, no `asc`/`none` states.
 * - The viewer's in-list row gets an accent tint + `YOU` tag (no per-row style hook exists on
 *   `SortableTable`).
 * - A pinned extra `<tr>` is appended inside `<tbody>` (not `<tfoot>`) when the viewer falls
 *   outside the visible top 100 for the active sort — `SortableTable` has no footer/extra-row slot.
 *
 * Row click (Key Implementation Note 7 / Phase 3 Critical Issue 3): both entity types navigate to
 * `/world/kingdoms/{kingdomId}` — for a Kingdom row, `kingdomId = row.id`; for a Province row,
 * `kingdomId = row.location` (already `"kingdom:island"`), landing on that province's own
 * kingdom's roster rather than a fabricated Target Finder handoff.
 *
 * Header row is `position: sticky; top: 0`, matching the approved mockup's CSS. No offset is
 * needed and no `SiblingRow` collision is possible: `SiblingRow` only mounts when
 * `platform === 'mobile'` (`Layout.tsx`'s `showSiblingChrome = platform === 'mobile' &&
 * isSiblingRoute`), while `RankingsTable` only ever renders inside `DesktopRankingsShell` — the
 * two are mutually exclusive via the same `useAppShellPlatform()` breakpoint and can never be
 * mounted at once. Desktop's own chrome above the content column (`Sidebar`) is a side column, not
 * top-of-page chrome, so nothing else competes for `top: 0` here.
 */
export function RankingsTable({ entries, viewerEntry, currentTick, sortBy, onSortChange }: RankingsTableProps) {
  const navigate = useNavigate();

  function handleRowClick(entry: RankingEntry) {
    const kingdomId = entry.entityType === 'kingdom' ? entry.id : entry.location;
    navigate(`/world/kingdoms/${kingdomId}`);
  }

  const viewerInEntries = viewerEntry != null && entries.some((e) => e.id === viewerEntry.id);
  const showPinnedRow = viewerEntry != null && !viewerInEntries;

  function renderRow(entry: RankingEntry, idx: number, pinned: boolean) {
    const protectedNow = entry.entityType === 'province' && isProtected(entry.protectionExpiresAtTick, currentTick);
    const isAlt = idx % 2 === 1;
    const rowBg = entry.isViewer ? YOU_TINT : isAlt ? COLORS.tableRowAlt : COLORS.card;

    return (
      <tr
        key={pinned ? `pinned-${entry.id}` : entry.id}
        onClick={() => handleRowClick(entry)}
        style={{
          backgroundColor: rowBg,
          cursor: 'pointer',
          ...(pinned ? { borderTop: `2px solid ${COLORS.accentStrong}` } : {}),
        }}
      >
        <td
          style={{
            padding: '9px 10px',
            borderBottom: `1px solid ${COLORS.borderBar}`,
            fontWeight: 700,
            color: entry.position <= 3 ? COLORS.gold : COLORS.text.secondary,
            width: 44,
          }}
        >
          {entry.position}
        </td>
        <td style={{ padding: '9px 10px', borderBottom: `1px solid ${COLORS.borderBar}` }}>
          <span style={{ fontWeight: 600, color: COLORS.text.primary }}>
            {entry.name} ({entry.location})
          </span>
          {entry.isViewer && (
            <span style={{ fontSize: 10, fontWeight: 700, color: COLORS.accentStrong, marginLeft: 6 }}>YOU</span>
          )}
          {protectedNow && (
            <span style={{ color: COLORS.positive, marginLeft: 6, fontSize: 11, fontWeight: 600 }}>Protected</span>
          )}
        </td>
        {RANKING_STATS.map((stat) => {
          const active = stat.key === sortBy;
          return (
            <td
              key={stat.key}
              style={{
                padding: '9px 10px',
                borderBottom: `1px solid ${COLORS.borderBar}`,
                textAlign: 'right',
                fontVariantNumeric: 'tabular-nums',
                fontWeight: active ? 700 : 400,
                color: active ? (stat.key === 'networth' ? COLORS.gold : COLORS.text.primary) : COLORS.text.secondary,
              }}
            >
              {formatNumber(rankingStatValue(entry, stat.key))}
            </td>
          );
        })}
      </tr>
    );
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, color: COLORS.text.secondary }}>
        <thead>
          <tr style={{ backgroundColor: COLORS.sectionHeader }}>
            <th
              scope="col"
              style={{
                position: 'sticky',
                top: 0,
                zIndex: 2,
                padding: '8px 10px',
                textAlign: 'left',
                fontWeight: 600,
                color: COLORS.text.tertiary,
                borderBottom: `1px solid ${COLORS.border}`,
                backgroundColor: COLORS.sectionHeader,
                width: 44,
              }}
            >
              #
            </th>
            <th
              scope="col"
              style={{
                position: 'sticky',
                top: 0,
                zIndex: 2,
                padding: '8px 10px',
                textAlign: 'left',
                fontWeight: 600,
                color: COLORS.text.tertiary,
                borderBottom: `1px solid ${COLORS.border}`,
                backgroundColor: COLORS.sectionHeader,
              }}
            >
              Name
            </th>
            {RANKING_STATS.map((stat) => {
              const isActive = stat.key === sortBy;
              return (
                <th
                  key={stat.key}
                  scope="col"
                  aria-sort={isActive ? 'descending' : 'none'}
                  style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    padding: '8px 10px',
                    textAlign: 'right',
                    fontWeight: 600,
                    color: COLORS.text.tertiary,
                    borderBottom: `1px solid ${COLORS.border}`,
                    backgroundColor: COLORS.sectionHeader,
                    whiteSpace: 'nowrap',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => onSortChange(stat.key)}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      margin: 0,
                      font: 'inherit',
                      color: isActive ? COLORS.accentStrong : 'inherit',
                      cursor: 'pointer',
                      userSelect: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    {stat.label} <span aria-hidden="true">▾</span>
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {entries.length === 0 && !showPinnedRow ? (
            <tr>
              <td
                colSpan={2 + RANKING_STATS.length}
                style={{ padding: 24, textAlign: 'center', color: COLORS.text.muted }}
              >
                No provinces/kingdoms found in this scope
              </td>
            </tr>
          ) : (
            entries.map((entry, idx) => renderRow(entry, idx, false))
          )}
          {showPinnedRow && renderRow(viewerEntry as RankingEntry, entries.length, true)}
        </tbody>
      </table>
    </div>
  );
}
